import { useCallback, useState } from "react";
import "../../common/styles/list-style.css";
import "./LogsListView.css";
import {
  Datagrid,
  TextField,
  Filter,
  Identifier,
  FunctionField,
  SearchInput,
  Record,
  SimpleList,
  Responsive,
  SelectInput,
} from "react-admin";
import { Box, Typography, Tooltip, Theme, useTheme } from "@material-ui/core";
import { Void } from "../../common/data-grid-fields/Void";
import dataGridFields from "../../common/data-grid-fields";
import { useAppData } from "../../../common/hooks/useAppData";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import resourceNames from "../../../common/data/resourceNames";
import { AppbarGroupsFilter } from "../../common/data-grid-inputs/AppBarGroupsFilter";
import { addDays, endOfToday } from "date-fns";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";
import logs from ".";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import { AppBarTenantSelector } from "../../common/data-grid-inputs/AppBarTenantSelector";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { AppbarCompaniesFilter } from "../../common/data-grid-inputs/AppBarCompaniesFilter";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import {
  AndroidOutlined,
  Apple,
  HelpOutlineOutlined,
} from "@material-ui/icons";
import ShowIcon from "@material-ui/icons/RemoveRedEye";
import { Link } from "react-router-dom";
import { RecordStatus } from "../../../common/data/service-proxies";

const driverStatuses = [
  { id: null, name: "All" },
  { id: RecordStatus.Active, name: "Active" },
  { id: RecordStatus.Inactive, name: "Inactive" },
];

const LogsListFilter = (props: any) => {
  useSearchFields(props, ["fullName"]);
  useResetListSearchField(props);
  return (
    <Filter {...props}>
      <SearchInput
        className="list-view-filter search-box"
        source="q"
        alwaysOn
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        className="list-view-filter"
        label="resources.hos-logs.labels.dateFrom"
        source="dateFrom"
        alwaysOn
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        className="list-view-filter"
        label="resources.hos-logs.labels.dateTo"
        source="dateTo"
        alwaysOn
      />
      <SelectInput
        className="list-view-filter"
        label="Driver status"
        source="driverStatus"
        choices={driverStatuses}
        allowEmpty={false}
        alwaysOn
      />
    </Filter>
  );
};

export const LogsListView = (props: any) => {
  const timeZones = useAppData((x) => x.timeZones) ?? [];
  const [selectedGroup, setSelectedGroup] = useState<Identifier>("");
  const permissions = useActionPermissions();
  const [selectedCompanies, setSelectedCompanies] = useState<Array<string>>([]);
  const handleSelectedCompaniesChange = useCallback((e: string[]) => {
    setSelectedCompanies(e);
  }, []);
  const theme: Theme = useTheme();

  const getFilter = () => {
    let retVal: any = {};
    if (selectedGroup) {
      retVal.groupId = selectedGroup;
    }

    if (selectedCompanies?.length) {
      retVal.companyIds = selectedCompanies.join(",");
    }

    return retVal;
  };

  const renderPrimaryTextForSimpleList = (record: any) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {record.driverId + " | " + record.fullName}
        <FunctionField
          source="mobileAppType"
          label="resources.admin-drivers.labels.mobAppType"
          render={(record: Record | undefined) => (
            <Tooltip title="Mobile App">
              <Box className="mobile-app-container">
                {record?.mobileAppType === "Android" ? (
                  <>
                    <AndroidOutlined />
                    <Typography variant="body2">
                      {` ${record.mobileAppVersion}`}
                    </Typography>
                  </>
                ) : record?.mobileAppType === "AppleIos" ? (
                  <>
                    <Apple />
                    <Typography variant="body2">
                      {` ${record.mobileAppVersion}`}
                    </Typography>
                  </>
                ) : (
                  <Tooltip title="The driver hasn't logged in yet">
                    <HelpOutlineOutlined />
                  </Tooltip>
                )}
              </Box>
            </Tooltip>
          )}
        />
      </div>
    );
  };

  const renderSecondaryTextForSimpleList = (record: any) => {
    return (
      <div
        style={{
          marginTop: "14px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="body2"
            style={{ fontWeight: "600", color: theme.palette.text.secondary }}
          >
            Last activity:
          </Typography>
          <FunctionField<any>
            style={{ color: theme.palette.text.secondary }}
            source="homeTerminalTimeZone"
            label="resources.hos-logs.labels.homeTerminalTimeZone"
            render={() =>
              timeZones.find((x) => x.id === record?.homeTerminalTimeZone)?.name
            }
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="body2"
            style={{ fontWeight: "600", color: theme.palette.text.secondary }}
          >
            Home terminal zone:
          </Typography>
          <FunctionField<any>
            style={{ color: theme.palette.text.secondary }}
            source="homeTerminalTimeZone"
            label="resources.hos-logs.labels.homeTerminalTimeZone"
            render={() =>
              timeZones.find((x) => x.id === record?.homeTerminalTimeZone)?.name
            }
          />
        </div>
      </div>
    );
  };

  return (
    <div className="content-container">
      <AppbarCompaniesFilter
        selectedCompanies={selectedCompanies}
        onSelectedCompaniesChange={handleSelectedCompaniesChange}
      />
      <AppbarGroupsFilter
        selectedGroup={selectedGroup}
        onSelectedGroupChange={(id) => setSelectedGroup(id)}
      />
      {permissions &&
      (permissions[permissionNames.hos.logs.delete] ||
        permissions[permissionNames.hos.logs.reassign]) ? (
        <AppBarTenantSelector />
      ) : null}
      <DefaultProLogsList
        title="resources.hos-logs.name"
        bulkActionButtons={false}
        canExport={false}
        actions={<Void />}
        filters={<LogsListFilter />}
        filterDefaultValues={{
          dateFrom: addDays(endOfToday(), -7).toISOString(),
          dateTo: endOfToday().toISOString(),
        }}
        filter={getFilter()}
        empty={<ProLogsEmpty iconType={logs.icon} />}
        sort={{ field: "fullName", order: "ASC" }}
        {...props}
      >
        <Responsive
          small={
            <>
              <SimpleList
                className="simple-list-wrapper"
                linkType={false}
                primaryText={renderPrimaryTextForSimpleList}
                secondaryText={renderSecondaryTextForSimpleList}
                rightIcon={(record) => (
                  <Link to={`/${resourceNames.HOS_DRIVER_LOGS}/${record.id}`}>
                    <ShowIcon
                      style={{
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Link>
                )}
              />
            </>
          }
          medium={
            <Datagrid
              rowClick={(id, _basePath, _record) => {
                return `/${resourceNames.HOS_DRIVER_LOGS}/${id}`;
              }}
            >
              <TextField
                source="driverId"
                label="resources.drivers.labels.driverId"
              />
              <TextField
                source="fullName"
                label="resources.hos-logs.labels.fullName"
              />
              <FunctionField
                source="mobileAppType"
                label="resources.admin-drivers.labels.mobAppType"
                render={(record: Record | undefined) => (
                  <Box className="mobile-app-container">
                    {record?.mobileAppType === "Android" ? (
                      <>
                        <AndroidOutlined />
                        <Typography variant="body2">
                          {` ${record.mobileAppVersion}`}
                        </Typography>
                      </>
                    ) : record?.mobileAppType === "AppleIos" ? (
                      <>
                        <Apple />
                        <Typography variant="body2">
                          {` ${record.mobileAppVersion}`}
                        </Typography>
                      </>
                    ) : (
                      <Tooltip title="The driver hasn't logged in yet">
                        <HelpOutlineOutlined />
                      </Tooltip>
                    )}
                  </Box>
                )}
              />
              {/* <FunctionField<any>
            source="hasViolations"
            headerClassName="centered-content"
            label="resources.hos-logs.labels.hasViolations"
            render={(record) => (
              <Box className="centered-content">
                {record?.hasViolations ? (
                  <ReportIcon
                    className={record.status}
                    style={{
                      color: theme.palette.error.main,
                    }}
                  />
                ) : (
                  <IndeterminateCheckBoxIcon
                    className={record?.status}
                    style={{
                      color: theme.palette.success.main,
                    }}
                  />
                )}
              </Box>
            )}
          /> */}
              <dataGridFields.TimeField
                source="lastSync"
                inputType="datetime"
                outputType="long"
                displayDays
                label="resources.hos-logs.labels.lastSync"
              />
              <FunctionField<any>
                source="homeTerminalTimeZone"
                label="resources.hos-logs.labels.homeTerminalTimeZone"
                render={(record) =>
                  timeZones.find((x) => x.id === record?.homeTerminalTimeZone)
                    ?.name
                }
              />
            </Datagrid>
          }
        />
      </DefaultProLogsList>
    </div>
  );
};
